import { useState, useEffect } from 'react';
import { useI18n } from '../../../../../i18n';
import {
  fetchPersona,
  fetchPersonaOrganigrama,
  type PersonaDetalle,
  type PersonaOrgChart,
} from '../../../../shared/services/api';

interface PersonaDrawerProps {
  personaId: string | null;
  onClose: () => void;
}

const TIPO_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  EMPLEADO: { bg: '#EFF6FF', text: '#1D4ED8' },
  COLABORADOR: { bg: '#FFF7ED', text: '#C2410C' },
  EXTERNO: { bg: '#F5F3FF', text: '#7C3AED' },
};

const ESTADO_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  ACTIVO: { bg: '#F0FDF4', text: '#16A34A' },
  BAJA: { bg: '#FEF2F2', text: '#DC2626' },
};

function getInitials(nombre: string, apellidos: string): string {
  return `${nombre.charAt(0)}${apellidos.charAt(0)}`.toUpperCase();
}

export function PersonaDrawer({ personaId, onClose }: PersonaDrawerProps) {
  const { t } = useI18n();
  const [detalle, setDetalle] = useState<PersonaDetalle | null>(null);
  const [orgChart, setOrgChart] = useState<PersonaOrgChart | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!personaId) {
      setDetalle(null);
      setOrgChart(null);
      return;
    }
    setLoading(true);
    Promise.all([
      fetchPersona(personaId).then((r) => r.data),
      fetchPersonaOrganigrama(personaId)
        .then((r) => r.data)
        .catch(() => null),
    ])
      .then(([det, org]) => {
        setDetalle(det);
        setOrgChart(org);
      })
      .catch(() => setDetalle(null))
      .finally(() => setLoading(false));
  }, [personaId]);

  if (!personaId) return null;

  const pd = t.people.personaDrawer;

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.25)',
          zIndex: 900,
        }}
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: 380,
          height: '100vh',
          background: '#fff',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          zIndex: 901,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #E5E7EB',
            background: '#F8FAFC',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: '#0E243E' }}>{pd.title}</span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: '#6B7280',
              fontSize: 18,
              lineHeight: 1,
            }}
            aria-label={t.common.cancelar}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 13 }}>
              {t.people.loadingPerson}
            </div>
          )}

          {!loading && !detalle && (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 13 }}>
              {t.people.personNotFound}
            </div>
          )}

          {!loading && detalle && (
            <>
              {/* Avatar + Name + Badges */}
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 20 }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 14,
                    background: detalle.persona.fotoUrl
                      ? `url(${detalle.persona.fotoUrl}) center/cover`
                      : '#1F3864',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {!detalle.persona.fotoUrl &&
                    getInitials(detalle.persona.nombre, detalle.persona.apellidos)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#1A1D23', marginBottom: 4 }}>
                    {detalle.persona.nombre} {detalle.persona.apellidos}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(() => {
                      const tb = TIPO_BADGE_COLORS[detalle.persona.tipoPersona] ?? {
                        bg: '#F3F4F6',
                        text: '#6B7280',
                      };
                      return (
                        <span
                          style={{
                            padding: '2px 10px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            background: tb.bg,
                            color: tb.text,
                          }}
                        >
                          {detalle.persona.tipoPersona}
                        </span>
                      );
                    })()}
                    {(() => {
                      const eb = ESTADO_BADGE_COLORS[detalle.persona.estado] ?? {
                        bg: '#F3F4F6',
                        text: '#6B7280',
                      };
                      return (
                        <span
                          style={{
                            padding: '2px 10px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            background: eb.bg,
                            color: eb.text,
                          }}
                        >
                          {detalle.persona.estado}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Contact */}
              <Section label={pd.contact}>
                {detalle.persona.email && (
                  <InfoRow label={t.people.email} value={detalle.persona.email} />
                )}
                {detalle.persona.telefono && (
                  <InfoRow label={t.people.phone} value={detalle.persona.telefono} />
                )}
              </Section>

              {/* Position + Department */}
              {detalle.puestoActual && (
                <Section label={pd.positionInfo}>
                  <InfoRow label={t.people.position} value={detalle.puestoActual.puesto.titulo} />
                  {detalle.puestoActual.departamento && (
                    <InfoRow
                      label={t.people.form.department}
                      value={detalle.puestoActual.departamento.nombre}
                    />
                  )}
                  {detalle.puestoActual.organizacion && (
                    <InfoRow
                      label={pd.organization}
                      value={detalle.puestoActual.organizacion.nombre}
                    />
                  )}
                </Section>
              )}

              {/* Centro */}
              {detalle.centros.length > 0 && (
                <Section label={t.people.centers}>
                  {detalle.centros.map((c) => (
                    <InfoRow
                      key={c.id}
                      label={c.centro.nombre}
                      value={c.centro.ciudad ?? ''}
                      badge={c.esPrincipal ? pd.main : undefined}
                    />
                  ))}
                </Section>
              )}

              {/* Mini org chart */}
              {orgChart && (orgChart.supervisor || orgChart.subordinados.length > 0) && (
                <Section label={pd.miniOrgChart}>
                  {orgChart.supervisor && (
                    <div style={{ marginBottom: 8 }}>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: '#9CA3AF',
                          textTransform: 'uppercase',
                          marginBottom: 4,
                        }}
                      >
                        {pd.supervisor}
                      </div>
                      <OrgChartNode
                        titulo={orgChart.supervisor.puesto.titulo}
                        persona={orgChart.supervisor.persona}
                      />
                    </div>
                  )}
                  {orgChart.subordinados.length > 0 && (
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: '#9CA3AF',
                          textTransform: 'uppercase',
                          marginBottom: 4,
                        }}
                      >
                        {pd.subordinates} ({orgChart.subordinados.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {orgChart.subordinados.map((s) => (
                          <OrgChartNode
                            key={s.puesto.id}
                            titulo={s.puesto.titulo}
                            persona={s.persona}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </Section>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#1F3864',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 8,
          paddingBottom: 4,
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value, badge }: { label: string; value: string; badge?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 0',
      }}
    >
      <span style={{ fontSize: 12, color: '#6B7280' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 13, color: '#1A1D23', fontWeight: 500, textAlign: 'right' }}>
          {value}
        </span>
        {badge && (
          <span
            style={{
              padding: '1px 6px',
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 600,
              background: '#EFF6FF',
              color: '#1D4ED8',
            }}
          >
            {badge}
          </span>
        )}
      </div>
    </div>
  );
}

function OrgChartNode({
  titulo,
  persona,
}: {
  titulo: string;
  persona: { id: string; nombre: string; apellidos: string; fotoUrl: string | null } | null;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        background: '#F8FAFC',
        borderRadius: 6,
        border: '1px solid #E5E7EB',
      }}
    >
      {persona && (
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: persona.fotoUrl ? `url(${persona.fotoUrl}) center/cover` : '#2F5496',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {!persona.fotoUrl && getInitials(persona.nombre, persona.apellidos)}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#1A1D23',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {titulo}
        </div>
        {persona && (
          <div style={{ fontSize: 11, color: '#6B7280' }}>
            {persona.nombre} {persona.apellidos}
          </div>
        )}
      </div>
    </div>
  );
}
